import { AlertTriangle, CheckCircle2, Wrench, BookMarked } from "lucide-react";

const Eyebrow = ({ children }) => <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">{children}</div>;
const H2 = ({ children }) => <h2 className="display mt-3 text-3xl sm:text-4xl font-extrabold tracking-tighter text-slate-900 max-w-3xl">{children}</h2>;

export function Pains({ d }) {
  return (
    <section className="bg-cyanotype text-white" data-testid="discipline-pains">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 lg:py-20">
        <div className="mono text-[10px] tracking-[0.22em] uppercase text-orange-survey-light">Sound familiar?</div>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
          {d.pains.map((p) => (
            <div key={p.title} className="bg-cyanotype p-7" data-reveal>
              <AlertTriangle className="h-5 w-5 text-safety" strokeWidth={1.75} />
              <h3 className="display mt-4 text-xl font-bold tracking-tight">{p.title}</h3>
              <p className="mt-2 text-sm text-slate-300 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Deliverables({ d }) {
  return (
    <section className="bg-white border-b border-slate-200" data-testid="discipline-deliverables">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 lg:py-24">
        <Eyebrow>What we deliver</Eyebrow>
        <H2>{d.name}, scoped to exactly what your project needs.</H2>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-slate-200">
          {d.deliverables.map((x, i) => (
            <div key={x.title} className="group border-r border-b border-slate-200 p-7 hover:bg-paper-dark/30 transition-colors" data-reveal>
              <div className="flex items-center justify-between">
                <span className="mono text-[11px] text-orange-survey">{String(i + 1).padStart(2, "0")}</span>
                <CheckCircle2 className="h-4 w-4 text-slate-300 group-hover:text-emerald-700 transition-colors" />
              </div>
              <h3 className="display mt-4 text-lg font-bold tracking-tight text-slate-900">{x.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{x.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ListCard({ icon: Icon, title, items, testid }) {
  return (
    <div className="border border-slate-200 bg-white p-7" data-testid={testid}>
      <div className="flex items-center gap-2 mono text-[10px] tracking-[0.22em] uppercase text-slate-500">
        <Icon className="h-4 w-4 text-topo" /> {title}
      </div>
      <ul className="mt-5 space-y-3">
        {items.map((it) => (
          <li key={it} className="flex items-start gap-3 text-[15px] text-slate-800">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 bg-orange-survey" /> {it}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function StandardsSoftware({ d }) {
  return (
    <section className="bg-paper-dark/30 border-b border-slate-200" data-testid="discipline-standards">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <Eyebrow>Standards & software</Eyebrow>
          <H2>Your standards, your platform.</H2>
          <p className="mt-4 text-slate-600 leading-relaxed">We work inside your templates, naming conventions and file formats, so deliverables drop straight into your workflow.</p>
        </div>
        <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <ListCard icon={BookMarked} title="Standards we follow" items={d.standards} testid="discipline-standards-list" />
          <ListCard icon={Wrench} title="Software" items={d.software} testid="discipline-software-list" />
        </div>
      </div>
    </section>
  );
}

export function Process({ d }) {
  return (
    <section className="bg-white border-b border-slate-200" data-testid="discipline-process">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 lg:py-24">
        <Eyebrow>How it works</Eyebrow>
        <H2>Four steps from brief to issued drawings.</H2>
        <ol className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-8">
          {d.process.map((s, i) => (
            <li key={s.title} className="relative pt-6 border-t-2 border-slate-900" data-reveal>
              <span className="absolute -top-3 left-0 bg-white pr-3 mono text-[11px] tracking-[0.2em] text-orange-survey">STEP {i + 1}</span>
              <h3 className="display text-lg font-bold tracking-tight text-slate-900">{s.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.body}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
